import { ChangeDetectionStrategy, Component, inject, Injectable, NgModule, viewChild, ViewEncapsulation } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import {
    MatDialogConfig,
    MatDialogRef,
    MAT_DIALOG_DATA,
    MatDialogModule,
    MatDialog,
    MAT_DIALOG_DEFAULT_OPTIONS,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInput, MatInputModule } from '@angular/material/input';
import {
    CoreDialogModule,
    PCPS_DIALOGS_DEFAULT_OPTIONS,
    PCPSDialogsDefaultOptions,
    ScreenSizeService,
} from '@polk_county_public_schools/ngx-pcps-dialogs/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, NgForm, ReactiveFormsModule, Validators } from '@angular/forms';
import { Observable } from 'rxjs';
import { AuthService } from '../../../services/auth/auth-svc.ng';
import { SplashDialogModule, SplashService } from '@polk_county_public_schools/ngx-pcps-dialogs/splash';
import { SnackBarModule, SnackBarService } from '@polk_county_public_schools/ngx-pcps-dialogs/snackbar';
import { ErrorService } from '../../../services/error/error.service';
import { MessageBoxDialogModule, MessageBoxDialogService } from '@polk_county_public_schools/ngx-pcps-dialogs/messagebox';
import { UserService } from '../../../services/user/user-svc.ng';
import { LoginInfo } from '../../../services/auth/auth';

// #region Custom Interfaces

export interface LoginDialogOptions extends MatDialogConfig {
    CancelText?: string;
    ClassicCancel?: boolean;
    MessageText?: string;
    OKText?: string;
    TitleText?: string;

    LastUsername?: string;
}

interface LoginFormGroup {
    email: FormControl<string>;
    password: FormControl<string>;
}

// #endregion

@Component({
    selector: 'login-dialog',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    imports: [
        MatDialogModule,
        MatButtonModule,
        MatIconModule,
        MatFormFieldModule,
        MatInputModule,
        ReactiveFormsModule,
        SnackBarModule,
        SplashDialogModule,
        MessageBoxDialogModule,
    ],
    host: {
        class: 'block relative',
    },
    templateUrl: 'login-dlg.ng.html',
})
export class LoginDialogComponent {
    // #region Injects / View Children

    protected readonly authSvc = inject(AuthService);
    protected readonly dialogRef = inject(MatDialogRef<LoginDialogComponent>);
    protected readonly data = inject<LoginDialogOptions>(MAT_DIALOG_DATA);
    protected readonly emailCtrl = viewChild<MatInput>('emailctrl');
    protected readonly errorSvc = inject(ErrorService);
    protected readonly fb = inject(FormBuilder);
    protected readonly msgBoxDlgSvc = inject(MessageBoxDialogService);
    protected readonly ngForm = viewChild<NgForm>('grp');
    protected readonly pcpsDefaults = inject<PCPSDialogsDefaultOptions>(PCPS_DIALOGS_DEFAULT_OPTIONS, { optional: true });
    protected readonly snackSvc = inject(SnackBarService);
    protected readonly splashSvc = inject(SplashService);
    protected readonly userSvc = inject(UserService);

    // #endregion

    // #region Properties

    protected readonly canceltext = this.data?.CancelText ?? 'Cancel';
    protected readonly classicCancel = this.data?.ClassicCancel ?? this.pcpsDefaults?.useClassicCancel ?? false;
    protected readonly message = this.data?.MessageText ?? '';
    protected readonly oktext = this.data?.OKText ?? 'OK';
    protected readonly title = this.data?.TitleText ?? 'Login...';

    protected readonly loginForm = this.fb.group<LoginFormGroup>({
        email: this.fb.control<string>('', {
            nonNullable: true,
            validators: [Validators.required, Validators.email, Validators.maxLength(150)],
        }),
        password: this.fb.control<string>('', {
            nonNullable: true,
            validators: [Validators.required],
        }),
    });

    // #endregion

    // #region Create Account

    createAccount() {
        // Dismiss this dialog, and the caller will have to monitor for launching the create dialog.
        this.dialogRef.close('create');
    }

    // #endregion

    // #region OK Handler

    OKHandler() {
        if (!this.loginForm.valid) {
            // Mark all controls as touched to get errors to display.
            this.loginForm.markAllAsTouched();
            return;
        }

        const loginInfo = this.loginForm.value;

        // Grab the screen and window resolutions
        const scr = window.screen.width + 'x' + window.screen.height;
        const win = window.innerWidth + 'x' + window.innerHeight;

        // Now we try to login.
        this.splashSvc
            .Open({
                TitleText: 'Please Wait',
                MessageText: 'Logging in...',
            })
            .subscribe((closer) => {
                this.authSvc.validateUser(loginInfo.email, loginInfo.password, scr, win).subscribe({
                    next: (info) => {
                        closer?.close();

                        // Set the user in the user service. This should ripple everything else from here.
                        this.userSvc.setUser(info);

                        // Close this dialog
                        this.dialogRef.close('logged_in');
                    },
                    error: (err) => {
                        closer?.close();

                        // Check the error message. If it indicates an unverified account, we can display a special alert about that.
                        const errmsg = this.errorSvc.retrieveServiceErrorMessage(err);

                        if (errmsg?.includes('[Code C]')) {
                            this.msgBoxDlgSvc
                                .Open({
                                    TitleText: 'Unverified Account',
                                    MessageText: `We're sorry, but the account you are trying to login with has not yet been verified. Please check your email or junk folder for an email from jauction@karoltech.net containing the instructions and link to use to verify your account. Once that has been verified, you can login using this form. Thank you!`,
                                    OKText: 'OK',
                                })
                                .subscribe(() => {
                                    // Clear the form.
                                    this.loginForm.reset();
                                    this.ngForm()?.resetForm();

                                    // Move focus to email field.
                                    this.emailCtrl()?.focus();
                                });

                            return;
                        }

                        // Just display error like usual.
                        this.errorSvc.handleServiceError(err);
                    },
                });
            });
    }

    // #endregion
}

@Injectable()
export class LoginDialogService {
    // #region Injects

    protected readonly matDialog = inject(MatDialog);
    protected readonly mediaChange = inject(ScreenSizeService);
    protected readonly defaultOpts = inject<MatDialogConfig>(MAT_DIALOG_DEFAULT_OPTIONS, { optional: true });

    // #endregion

    // #region Properties

    protected readonly smallMediaSize = this.mediaChange?.ScreenSize?.IsSmallDisplay || false;

    // #endregion

    // #region Close All Dialogs Service Call

    CloseAllDialogs(): void {
        this.matDialog.closeAll();
    }

    // #endregion

    // #region Open

    Open(options?: LoginDialogOptions): Observable<string | null> {
        const dlgoptions: LoginDialogOptions = {
            data: {
                TitleText: 'Log In...',
                MessageText: '',
                OKText: 'OK',
                CancelText: 'Cancel',
                ...options,
            },
            // Passthrus
            id: options?.id ?? this.defaultOpts?.id,
            role: options?.role ?? this.defaultOpts?.role ?? 'dialog',
            panelClass: !!options?.panelClass
                ? Array.isArray(options.panelClass)
                    ? options.panelClass
                    : [options.panelClass]
                : (this.defaultOpts?.panelClass ?? ''),
            hasBackdrop: options?.hasBackdrop ?? this.defaultOpts?.hasBackdrop ?? true,
            backdropClass: options?.backdropClass ?? this.defaultOpts?.backdropClass ?? '',
            disableClose: options?.disableClose != null ? options.disableClose : (this.defaultOpts?.disableClose ?? false),
            width: options?.width ?? this.defaultOpts?.width ?? '',
            height: options?.height ?? this.defaultOpts?.height ?? '',
            minWidth: options?.minWidth ?? this.defaultOpts?.minWidth,
            minHeight: options?.minHeight ?? this.defaultOpts?.minHeight,
            maxWidth: options?.maxWidth ?? this.defaultOpts?.maxWidth ?? '80vw',
            maxHeight: options?.maxHeight ?? this.defaultOpts?.maxHeight,
            position: options?.position ?? this.defaultOpts?.position,
            direction: options?.direction ?? this.defaultOpts?.direction,
            ariaDescribedBy: options?.ariaDescribedBy ?? this.defaultOpts?.ariaDescribedBy ?? null,
            ariaLabelledBy: options?.ariaLabelledBy ?? this.defaultOpts?.ariaLabelledBy ?? null,
            ariaLabel: options?.ariaLabel ?? this.defaultOpts?.ariaLabel ?? null,
            ariaModal: options?.ariaModal ?? this.defaultOpts?.ariaModal ?? true,
            autoFocus: options?.autoFocus ?? this.defaultOpts?.autoFocus ?? 'first-tabbable',
            restoreFocus: options?.restoreFocus ?? this.defaultOpts?.restoreFocus ?? true,
            delayFocusTrap: options?.delayFocusTrap ?? this.defaultOpts?.delayFocusTrap ?? true,
            scrollStrategy: options?.scrollStrategy ?? this.defaultOpts?.scrollStrategy,
            closeOnNavigation: options?.closeOnNavigation ?? this.defaultOpts?.closeOnNavigation ?? true,
            enterAnimationDuration: options?.enterAnimationDuration ?? this.defaultOpts?.enterAnimationDuration,
            exitAnimationDuration: options?.exitAnimationDuration ?? this.defaultOpts?.exitAnimationDuration,
        };

        // if (this.smallMediaSize) {
        //     dlgoptions.width = '80vw';
        //     dlgoptions.panelClass = [...(dlgoptions.panelClass as string[])];
        // } else {
        //     dlgoptions.panelClass = [...(dlgoptions.panelClass as string[])];
        // }

        const openHandle = this.matDialog.open<LoginDialogComponent>(LoginDialogComponent, dlgoptions);

        return openHandle.afterClosed();
    }

    // #endregion
}

@NgModule({
    imports: [LoginDialogComponent, CoreDialogModule],
    declarations: [],
    exports: [LoginDialogComponent],
    providers: [LoginDialogService],
})
export class LoginDialogModule {}
